import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { adminApi, type ArticleSource, type SyncSummaryCard } from '@/api';
import { SyncPreview } from './SyncPreview';

type CardForm = {
  id?: string;
  title: string;
  sub_title: string;
  sections: Array<{ type: 'text' | 'image'; content: string }>;
  key_takeaway: string;
  order: number;
};

function emptyCard(order: number): CardForm {
  return {
    title: '',
    sub_title: '',
    sections: [{ type: 'text', content: '' }],
    key_takeaway: '',
    order,
  };
}

export function SyncForm() {
  const { id } = useParams<'id'>();
  const navigate = useNavigate();
  const isNew = !id;

  const [sources, setSources] = useState<ArticleSource[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [generateUrl, setGenerateUrl] = useState('');
  const [generateContent, setGenerateContent] = useState('');
  const [generating, setGenerating] = useState(false);

  const [articleSourceId, setArticleSourceId] = useState('');
  const [articleTitle, setArticleTitle] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [publishedAt, setPublishedAt] = useState('');
  const [published, setPublished] = useState(false);
  const [cards, setCards] = useState<CardForm[]>([]);
  const [showPreview, setShowPreview] = useState(true);
  const [previewReaderMode, setPreviewReaderMode] = useState(false);

  const [loadedCardIds, setLoadedCardIds] = useState<string[]>([]);

  useEffect(() => {
    adminApi.articleSources.list().then(setSources).catch(() => {});
    if (isNew && sources.length) setArticleSourceId(sources[0]!.id);
  }, [isNew, sources.length]);

  useEffect(() => {
    if (isNew) return;
    adminApi.syncArticles.get(id!).then((a) => {
      setArticleSourceId(a.articleSourceId);
      setArticleTitle(a.articleTitle);
      setSourceUrl(a.sourceUrl);
      setThumbnailUrl(a.thumbnailUrl ?? '');
      setPublishedAt(a.publishedAt ? a.publishedAt.slice(0, 10) : '');
      setPublished(a.published ?? false);
      const cardList = (a.summaryCards ?? []).sort((x, y) => (x.order ?? 0) - (y.order ?? 0));
      setCards(
        cardList.length
          ? cardList.map((c, i) => {
              const card = c as SyncSummaryCard & { subTitle?: string };
              return {
                id: card.id,
                title: card.title,
                sub_title: card.sub_title ?? card.subTitle ?? '',
                sections: card.sections?.length ? card.sections : [{ type: 'text' as const, content: '' }],
                key_takeaway: card.key_takeaway ?? '',
                order: i,
              };
            })
          : [emptyCard(0)],
      );
      setLoadedCardIds(cardList.map((c) => c.id).filter(Boolean) as string[]);
    }).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [id, isNew]);

  const handleGenerate = async () => {
    if (!articleSourceId || !generateUrl.trim()) {
      setError('Select a source and enter article URL');
      return;
    }
    setError(null);
    setGenerating(true);
    try {
      const result = await adminApi.ai.generateSync({
        articleSourceId,
        sourceUrl: generateUrl.trim(),
        articleContent: generateContent.trim() || undefined,
      });
      setArticleTitle(result.articleTitle);
      setSourceUrl(result.sourceUrl);
      setThumbnailUrl(result.thumbnailUrl ?? '');
      setPublishedAt(result.publishedAt?.slice(0, 10) ?? '');
      setCards(
        result.summaryCards.map((c, i) => ({
          title: c.title,
          sub_title: c.sub_title,
          sections: c.sections?.length ? c.sections : [{ type: 'text' as const, content: '' }],
          key_takeaway: c.key_takeaway ?? '',
          order: i,
        })),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    const validCards = cards.filter(
      (c) => c.title.trim() && c.sub_title.trim() && c.sections.some((s) => s.content.trim()),
    );
    if (validCards.length === 0) {
      setError('Add at least one card with title and sub_title');
      setSaving(false);
      return;
    }
    try {
      const dto = {
        articleSourceId,
        articleTitle: articleTitle.trim(),
        sourceUrl: sourceUrl.trim(),
        thumbnailUrl: thumbnailUrl.trim() || null,
        publishedAt: publishedAt.trim() ? publishedAt : null,
        published,
        summaryCards: validCards.map((c, i) => ({
          title: c.title.trim(),
          sub_title: c.sub_title.trim(),
          sections: c.sections.filter((s) => s.content.trim()),
          key_takeaway: c.key_takeaway.trim() || null,
          order: i,
        })),
      };
      if (isNew) {
        await adminApi.syncArticles.create(dto);
        navigate('/syncs');
      } else {
        await adminApi.syncArticles.update(id!, {
          articleSourceId,
          articleTitle: dto.articleTitle,
          sourceUrl: dto.sourceUrl,
          thumbnailUrl: dto.thumbnailUrl,
          publishedAt: dto.publishedAt,
          published,
        });
        const keptIds = validCards.filter((c) => c.id).map((c) => c.id!);
        for (const oldId of loadedCardIds) {
          if (!keptIds.includes(oldId)) {
            await adminApi.syncArticles.deleteCard(id!, oldId);
          }
        }
        for (const card of validCards) {
          if (card.id) {
            await adminApi.syncArticles.updateCard(id!, card.id, {
              title: card.title.trim(),
              sub_title: card.sub_title.trim(),
              sections: card.sections.filter((s) => s.content.trim()),
              key_takeaway: card.key_takeaway.trim() || null,
              order: card.order,
            });
          } else {
            await adminApi.syncArticles.createCard(id!, {
              title: card.title.trim(),
              sub_title: card.sub_title.trim(),
              sections: card.sections.filter((s) => s.content.trim()),
              key_takeaway: card.key_takeaway.trim() || null,
              order: card.order,
            });
          }
        }
        navigate('/syncs');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addCard = () => setCards((prev) => [...prev, emptyCard(prev.length)]);
  const removeCard = (idx: number) => setCards((prev) => prev.filter((_, i) => i !== idx));
  const moveCard = (idx: number, dir: 'up' | 'down') => {
    const next = [...cards];
    const swap = dir === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap]!, next[idx]!];
    setCards(next.map((c, i) => ({ ...c, order: i })));
  };
  const updateCard = (idx: number, patch: Partial<CardForm>) => {
    setCards((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };
  const addSection = (cardIdx: number) => {
    updateCard(cardIdx, { sections: [...cards[cardIdx]!.sections, { type: 'text', content: '' }] });
  };
  const updateSection = (cardIdx: number, secIdx: number, patch: Partial<{ type: 'text' | 'image'; content: string }>) => {
    const card = cards[cardIdx]!;
    const next = [...card.sections];
    next[secIdx] = { ...next[secIdx]!, ...patch };
    updateCard(cardIdx, { sections: next });
  };
  const removeSection = (cardIdx: number, secIdx: number) => {
    const card = cards[cardIdx]!;
    if (card.sections.length <= 1) return;
    updateCard(cardIdx, { sections: card.sections.filter((_, i) => i !== secIdx) });
  };
  const moveSection = (cardIdx: number, secIdx: number, direction: 'up' | 'down') => {
    const card = cards[cardIdx]!;
    const swap = direction === 'up' ? secIdx - 1 : secIdx + 1;
    if (swap < 0 || swap >= card.sections.length) return;
    const next = [...card.sections];
    [next[secIdx], next[swap]] = [next[swap]!, next[secIdx]!];
    updateCard(cardIdx, { sections: next });
  };

  if (loading) return <div className="text-slate-500">Loading…</div>;

  const sourceName = sources.find((s) => s.id === articleSourceId)?.name;
  const previewCards: SyncSummaryCard[] = cards.map((c, i) => ({
    id: c.id ?? `preview-${i}`,
    title: c.title,
    sub_title: c.sub_title,
    sections: c.sections.filter((s) => s.content.trim()),
    key_takeaway: c.key_takeaway || undefined,
    order: i,
  }));

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-4 flex-wrap">
        <Link to="/syncs" className="text-slate-600 hover:text-slate-900 text-sm">← Syncs</Link>
        <h1 className="text-2xl font-semibold text-slate-800">{isNew ? 'New sync article' : 'Edit sync article'}</h1>
      </div>

      {/* AI Generate */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <h2 className="text-lg font-medium text-slate-800 mb-3">Generate with AI</h2>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="min-w-[200px]">
            <label className="block text-sm font-medium text-slate-700 mb-1">Source</label>
            <select
              value={articleSourceId}
              onChange={(e) => setArticleSourceId(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2"
            >
              <option value="">Select source</option>
              {sources.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div className="min-w-[300px] flex-1">
            <label className="block text-sm font-medium text-slate-700 mb-1">Article URL</label>
            <input
              value={generateUrl}
              onChange={(e) => setGenerateUrl(e.target.value)}
              placeholder="https://..."
              className="w-full rounded-lg border border-slate-300 px-3 py-2"
            />
          </div>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating || !articleSourceId || !generateUrl.trim()}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-white font-medium hover:bg-indigo-700 disabled:opacity-50"
          >
            {generating ? 'Generating…' : 'Generate'}
          </button>
        </div>
        <div className="mt-3">
          <label className="block text-sm font-medium text-slate-700 mb-1">Or paste article content (if URL fetch fails)</label>
          <textarea
            value={generateContent}
            onChange={(e) => setGenerateContent(e.target.value)}
            placeholder="Paste article HTML or plain text..."
            rows={3}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h2 className="text-lg font-medium text-slate-800 mb-3">Article</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Source</label>
                <select
                  value={articleSourceId}
                  onChange={(e) => setArticleSourceId(e.target.value)}
                  required
                  className="w-full rounded-lg border border-slate-300 px-3 py-2"
                >
                  <option value="">Select source</option>
                  {sources.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                <input
                  value={articleTitle}
                  onChange={(e) => setArticleTitle(e.target.value)}
                  required
                  className="w-full rounded-lg border border-slate-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Source URL</label>
                <input
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  required
                  type="url"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Thumbnail URL (optional)</label>
                <input
                  value={thumbnailUrl}
                  onChange={(e) => setThumbnailUrl(e.target.value)}
                  type="url"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Published (YYYY-MM-DD, optional)</label>
                <input
                  value={publishedAt}
                  onChange={(e) => setPublishedAt(e.target.value)}
                  type="date"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="published"
                  checked={published}
                  onChange={(e) => setPublished(e.target.checked)}
                  className="rounded border-slate-300"
                />
                <label htmlFor="published" className="text-sm font-medium text-slate-700">
                  Live (visible to users)
                </label>
              </div>
            </div>

            <h2 className="text-lg font-medium text-slate-800 mt-8 mb-3">Summary Cards</h2>
            <div className="space-y-4">
              {cards.map((card, idx) => (
                <div key={idx} className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-600">Card {idx + 1}</span>
                    <div className="flex gap-1">
                      <button type="button" onClick={() => moveCard(idx, 'up')} disabled={idx === 0} className="rounded border px-2 py-0.5 text-xs disabled:opacity-40">↑</button>
                      <button type="button" onClick={() => moveCard(idx, 'down')} disabled={idx === cards.length - 1} className="rounded border px-2 py-0.5 text-xs disabled:opacity-40">↓</button>
                      <button type="button" onClick={() => removeCard(idx)} className="rounded border border-red-200 px-2 py-0.5 text-xs text-red-600">Delete</button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <input
                      value={card.title}
                      onChange={(e) => updateCard(idx, { title: e.target.value })}
                      placeholder="Card title"
                      className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                    />
                    <input
                      value={card.sub_title}
                      onChange={(e) => updateCard(idx, { sub_title: e.target.value })}
                      placeholder="Sub-title / category"
                      className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                    />
                    {card.sections.map((sec, si) => (
                      <div key={si} className="flex gap-2 items-start">
                        <div className="flex flex-col gap-0.5">
                          <button type="button" onClick={() => moveSection(idx, si, 'up')} disabled={si === 0} className="rounded border border-slate-300 px-2 py-0.5 text-xs disabled:opacity-40 hover:bg-slate-50" title="Move section up">↑</button>
                          <button type="button" onClick={() => moveSection(idx, si, 'down')} disabled={si === card.sections.length - 1} className="rounded border border-slate-300 px-2 py-0.5 text-xs disabled:opacity-40 hover:bg-slate-50" title="Move section down">↓</button>
                        </div>
                        <select
                          value={sec.type}
                          onChange={(e) => updateSection(idx, si, { type: e.target.value as 'text' | 'image' })}
                          className="rounded border border-slate-300 px-2 py-1 text-sm mt-0.5"
                        >
                          <option value="text">Text</option>
                          <option value="image">Image</option>
                        </select>
                        {sec.type === 'text' ? (
                          <textarea
                            value={sec.content}
                            onChange={(e) => updateSection(idx, si, { content: e.target.value })}
                            placeholder="Content"
                            rows={2}
                            className="flex-1 rounded border border-slate-300 px-3 py-2 text-sm"
                          />
                        ) : (
                          <input
                            value={sec.content}
                            onChange={(e) => updateSection(idx, si, { content: e.target.value })}
                            placeholder="Image URL"
                            className="flex-1 rounded border border-slate-300 px-3 py-2 text-sm"
                          />
                        )}
                        <button type="button" onClick={() => removeSection(idx, si)} disabled={card.sections.length <= 1} className="rounded border px-2 text-xs disabled:opacity-40 mt-0.5" title="Remove section">×</button>
                      </div>
                    ))}
                    <button type="button" onClick={() => addSection(idx)} className="text-sm text-indigo-600 hover:underline">
                      + Add section
                    </button>
                    <input
                      value={card.key_takeaway}
                      onChange={(e) => updateCard(idx, { key_takeaway: e.target.value })}
                      placeholder="Key takeaway (optional)"
                      className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              ))}
              <button type="button" onClick={addCard} className="rounded-lg border border-dashed border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                + Add card
              </button>
            </div>

            {error && <div className="rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm mt-4">{error}</div>}
            <div className="flex gap-3 mt-6">
              <button
                type="submit"
                disabled={saving}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-white font-medium hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <Link to="/syncs" className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50">
                Cancel
              </Link>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-medium text-slate-800">Preview</h2>
              <div className="flex gap-2">
                <label className="flex items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    checked={showPreview}
                    onChange={(e) => setShowPreview(e.target.checked)}
                  />
                  Show
                </label>
                <label className="flex items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    checked={previewReaderMode}
                    onChange={(e) => setPreviewReaderMode(e.target.checked)}
                  />
                  Reader mode
                </label>
              </div>
            </div>
            {showPreview && (
              <SyncPreview
                articleTitle={articleTitle || 'Article title'}
                sourceUrl={sourceUrl || '#'}
                sourceName={sourceName}
                thumbnailUrl={thumbnailUrl || null}
                publishedAt={publishedAt || null}
                summaryCards={previewCards}
                readerMode={previewReaderMode}
              />
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
